import { useEffect, useRef, useState } from 'react'
import { getBelt, getBeltTheme, isChampion, calcRating, calcCatScore } from '../lib/helpers'
import { FRONT_STAT_KEYS } from '../lib/constants'

export default function ShareCard({ fighter, skills, competitions, categories, techniques, onClose }) {
  const canvasRef = useRef()
  const [imageUrl, setImageUrl] = useState(null)
  const [generating, setGenerating] = useState(true)
  const [shareMode, setShareMode] = useState('image') // 'image' | 'text'

  const belt = getBelt(fighter)
  const theme = isChampion(fighter, competitions)
    ? { bg: null, border: '#f0c010', color: '#3a1800', stops: [['#fff080',0],['#e8a000',20],['#fffaa0',45],['#c07800',70],['#f8d830',100]] }
    : { ...getBeltTheme(fighter), stops: getBeltGradientStops(belt.th) }

  const rating = calcRating(fighter, skills, competitions)
  const frontStats = FRONT_STAT_KEYS.map(({ label, cat }) => {
    const catData = categories?.find(c => c.name === cat)
    const techIds = catData ? techniques?.filter(t => t.category_id === catData.id).map(t => t.id) : []
    return { label, value: calcCatScore(skills, techIds) }
  })

  const goldCount = competitions?.filter(c => c.fighter_id === fighter.id && c.medal === 'gold').length || 0
  const silverCount = competitions?.filter(c => c.fighter_id === fighter.id && c.medal === 'silver').length || 0
  const bronzeCount = competitions?.filter(c => c.fighter_id === fighter.id && c.medal === 'bronze').length || 0

  useEffect(() => {
    generateImage()
  }, [])

  async function generateImage() {
    setGenerating(true)
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const W = 400, H = 560
    canvas.width = W
    canvas.height = H

    // Card background gradient
    const stops = theme.stops || [['#e0e0e0',0],['#aaa',100]]
    const grad = ctx.createLinearGradient(0, 0, W * 0.7, H)
    stops.forEach(([color, pct]) => grad.addColorStop(pct / 100, color))
    roundRect(ctx, 0, 0, W, H, 22, grad, theme.border, 4)

    // Face area (top 60%)
    const faceH = H * 0.60
    ctx.save()
    ctx.beginPath()
    roundRectPath(ctx, 0, 0, W, faceH + 20, { tl: 22, tr: 22, bl: 0, br: 0 })
    ctx.clip()

    if (fighter.photo_url) {
      try {
        const img = await loadImage(fighter.photo_url)
        // Cover fit, top-aligned
        const scale = Math.max(W / img.width, faceH / img.height)
        const iw = img.width * scale, ih = img.height * scale
        const ix = (W - iw) / 2, iy = 0
        ctx.drawImage(img, ix, iy, iw, ih)
      } catch {
        drawAvatarFallback(ctx, fighter, belt, W, faceH)
      }
    } else {
      drawAvatarFallback(ctx, fighter, belt, W, faceH)
    }

    // Gradient overlay on face bottom
    const overlay = ctx.createLinearGradient(0, faceH * 0.5, 0, faceH)
    overlay.addColorStop(0, 'rgba(0,0,0,0)')
    overlay.addColorStop(1, 'rgba(0,0,0,0.45)')
    ctx.fillStyle = overlay
    ctx.fillRect(0, 0, W, faceH)
    ctx.restore()

    // Shine sweep
    ctx.save()
    ctx.beginPath()
    roundRectPath(ctx, 0, 0, W, H, { tl: 22, tr: 22, bl: 22, br: 22 })
    ctx.clip()
    const shine = ctx.createLinearGradient(W * 0.2, 0, W * 0.55, H)
    shine.addColorStop(0, 'rgba(255,255,255,0)')
    shine.addColorStop(0.5, 'rgba(255,255,255,0.14)')
    shine.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = shine
    ctx.fillRect(0, 0, W, H)
    ctx.restore()

    // Rating number
    ctx.fillStyle = theme.color
    ctx.font = `bold ${W * 0.18}px Arial`
    ctx.fillText(rating, W * 0.06, H * 0.16)

    // BJJ label under rating
    ctx.font = `${W * 0.035}px Arial`
    ctx.globalAlpha = 0.75
    ctx.fillText('BJJ', W * 0.075, H * 0.21)
    ctx.globalAlpha = 1

    // Flag top right
    try {
      const flagUrl = getFlagImageUrl(fighter.country || 'HR')
      if (flagUrl) {
        const flagImg = await loadImage(flagUrl)
        ctx.save()
        roundRectPath(ctx, W - 62, 16, 46, 30, { tl: 3, tr: 3, bl: 3, br: 3 })
        ctx.clip()
        ctx.drawImage(flagImg, W - 62, 16, 46, 30)
        ctx.restore()
      }
    } catch {}

    // Name
    ctx.fillStyle = theme.color
    ctx.font = `bold ${W * 0.058}px Arial`
    ctx.textAlign = 'center'
    const name = `${fighter.first_name.toUpperCase()} ${fighter.last_name.toUpperCase()}`
    ctx.fillText(name, W / 2, faceH + 36)

    // Divider
    ctx.globalAlpha = 0.25
    ctx.strokeStyle = theme.color
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(W * 0.08, faceH + 48)
    ctx.lineTo(W * 0.92, faceH + 48)
    ctx.stroke()
    ctx.globalAlpha = 1

    // Stats row
    const statY = faceH + 90
    const statW = W / frontStats.length
    frontStats.forEach((s, i) => {
      const sx = i * statW + statW / 2
      ctx.fillStyle = theme.color
      ctx.font = `bold ${W * 0.058}px Arial`
      ctx.textAlign = 'center'
      ctx.fillText(s.value, sx, statY)
      ctx.font = `${W * 0.032}px Arial`
      ctx.globalAlpha = 0.65
      ctx.fillText(s.label, sx, statY + 20)
      ctx.globalAlpha = 1
    })

    // Medal pips
    const medals = [
      ...Array(Math.min(goldCount, 4)).fill('#d4a017'),
      ...Array(Math.min(silverCount, 3)).fill('#909090'),
      ...Array(Math.min(bronzeCount, 3)).fill('#a05020'),
    ]
    if (medals.length) {
      const pipSize = 14, gap = 6
      const totalW = medals.length * (pipSize + gap) - gap
      let px = (W - totalW) / 2
      medals.forEach(color => {
        ctx.beginPath()
        ctx.arc(px + pipSize / 2, H - 28, pipSize / 2, 0, Math.PI * 2)
        ctx.fillStyle = color
        ctx.fill()
        px += pipSize + gap
      })
    }

    // Watermark
    ctx.font = `${W * 0.028}px Arial`
    ctx.fillStyle = theme.color
    ctx.globalAlpha = 0.35
    ctx.textAlign = 'center'
    ctx.fillText('MyAthleteCard', W / 2, H - 10)
    ctx.globalAlpha = 1

    setImageUrl(canvas.toDataURL('image/png'))
    setGenerating(false)
  }

  const shareText = `🥋 ${fighter.first_name} ${fighter.last_name} — ${belt.n} belt · Rating ${rating}\n${goldCount > 0 ? `🥇 ${goldCount} gold medal${goldCount > 1 ? 's' : ''}` : ''}\n#BJJ #FighterCards #MartialArts`

  async function downloadImage() {
    const a = document.createElement('a')
    a.href = imageUrl
    a.download = `${fighter.first_name}-${fighter.last_name}-card.png`
    a.click()
  }

  async function copyText() {
    await navigator.clipboard.writeText(shareText)
    alert('Caption copied! Now paste it with your image.')
  }

  function shareNative() {
    if (navigator.share) {
      fetch(imageUrl)
        .then(r => r.blob())
        .then(blob => {
          const file = new File([blob], `${fighter.first_name}-card.png`, { type: 'image/png' })
          navigator.share({ title: `${fighter.first_name}'s Fighter Card`, text: shareText, files: [file] })
        })
        .catch(() => downloadImage())
    } else {
      downloadImage()
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }}>
      <div style={{
        background: '#1a1a2e', borderRadius: 20, padding: '1.5rem',
        width: '100%', maxWidth: 400, border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h3 style={{ flex: 1, color: '#fff', fontSize: 16, fontWeight: 700, margin: 0 }}>Share fighter card</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>×</button>
        </div>

        {/* Canvas (hidden, used to generate image) */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Preview */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
          {generating ? (
            <div style={{ width: 200, height: 280, borderRadius: 14, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Generating...
            </div>
          ) : (
            <img src={imageUrl} alt="Fighter card" style={{ width: 200, height: 'auto', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} />
          )}
        </div>

        {/* Caption preview */}
        <div style={{
          background: 'rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px',
          marginBottom: '1.25rem', fontSize: 13, color: 'rgba(255,255,255,0.7)',
          lineHeight: 1.6, whiteSpace: 'pre-line'
        }}>
          {shareText}
        </div>

        {/* Share buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button onClick={shareNative} disabled={generating} style={{
            gridColumn: '1/-1', padding: '12px', borderRadius: 10, border: 'none',
            background: 'linear-gradient(135deg,#1860a8,#0c4480)',
            color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
            opacity: generating ? 0.5 : 1
          }}>
            {navigator.share ? 'Share card' : 'Download card image'}
          </button>
          <button onClick={copyText} disabled={generating} style={{
            padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent', color: 'rgba(255,255,255,0.8)', fontSize: 13,
            cursor: 'pointer', fontFamily: 'inherit'
          }}>
            Copy caption
          </button>
          <button onClick={downloadImage} disabled={generating} style={{
            padding: '10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent', color: 'rgba(255,255,255,0.8)', fontSize: 13,
            cursor: 'pointer', fontFamily: 'inherit'
          }}>
            Download image
          </button>
        </div>

        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, textAlign: 'center', marginTop: 12, marginBottom: 0 }}>
          Download the image then post to Instagram, WhatsApp, TikTok
        </p>
      </div>
    </div>
  )
}

// Helpers
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function roundRect(ctx, x, y, w, h, r, fill, strokeColor, strokeWidth) {
  ctx.beginPath()
  roundRectPath(ctx, x, y, w, h, r)
  if (fill) { ctx.fillStyle = fill; ctx.fill() }
  if (strokeColor) { ctx.strokeStyle = strokeColor; ctx.lineWidth = strokeWidth; ctx.stroke() }
}

function roundRectPath(ctx, x, y, w, h, r) {
  const radii = typeof r === 'number' ? { tl: r, tr: r, bl: r, br: r } : r
  ctx.moveTo(x + radii.tl, y)
  ctx.lineTo(x + w - radii.tr, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radii.tr)
  ctx.lineTo(x + w, y + h - radii.br)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radii.br, y + h)
  ctx.lineTo(x + radii.bl, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radii.bl)
  ctx.lineTo(x, y + radii.tl)
  ctx.quadraticCurveTo(x, y, x + radii.tl, y)
  ctx.closePath()
}

function drawAvatarFallback(ctx, fighter, belt, W, H) {
  // Simple gradient background with initials
  const grad = ctx.createLinearGradient(0, 0, W, H)
  grad.addColorStop(0, belt.a1)
  grad.addColorStop(1, belt.a2)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = 'rgba(255,255,255,0.25)'
  ctx.font = `bold ${W * 0.3}px Arial`
  ctx.textAlign = 'center'
  ctx.fillText((fighter.first_name[0] + fighter.last_name[0]).toUpperCase(), W / 2, H * 0.65)
}

function getBeltGradientStops(th) {
  const stops = {
    wht: [['#f4f4f4',0],['#cccccc',40],['#f2f2f2',60],['#b0b0b0',100]],
    gry: [['#b0b0b0',0],['#686868',40],['#c0c0c0',60],['#484848',100]],
    yel: [['#f0cc30',0],['#b07800',40],['#f8e040',60],['#886000',100]],
    ora: [['#e87030',0],['#a03800',40],['#f08040',60],['#782800',100]],
    grn: [['#58aa58',0],['#206020',40],['#68b868',60],['#184018',100]],
    blu: [['#3888d0',0],['#0c4888',40],['#48a0e0',60],['#082868',100]],
    pur: [['#8070e0',0],['#3c30a0',40],['#9080f0',60],['#281878',100]],
    brn: [['#aa6830',0],['#603010',40],['#c07840',60],['#401800',100]],
    blk: [['#303030',0],['#0a0a0a',40],['#404040',60],['#080808',100]],
  }
  return stops[th] || stops.wht
}

function getFlagImageUrl(code) {
  // Use flagcdn.com for flag images in canvas
  return `https://flagcdn.com/w40/${code.toLowerCase()}.png`
}
